import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import ConfirmDialog from '../components/ConfirmDialog';
import { 
  FaArrowLeft, FaTruck, FaTrailer, FaUser, 
  FaCalendarAlt, FaCheckCircle, FaTimes, FaEye,
  FaUpload, FaSpinner, FaInfoCircle, FaEdit,
  FaTrash, FaClock, FaMapMarkerAlt, FaWeightHanging,
  FaMoneyBillWave, FaPlus, FaSave, FaFileAlt,
  FaImage, FaFilePdf, FaHistory
} from 'react-icons/fa';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import SubbieLayout from '../layouts/SubbieLayout';
import AdminLayout from '../layouts/AdminLayout';
import { unitLabel, unitSubLabel } from '../utils/unitLabels';

// ============================================
// SORTABLE ITEM COMPONENT
// ============================================
function SortableItem({ id, children, className, isAvailable }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id,
    disabled: !isAvailable,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isAvailable ? 'grab' : 'not-allowed',
  };

  return (
    <div ref={setNodeRef} style={style} {...(isAvailable ? attributes : {})} {...(isAvailable ? listeners : {})} className={className}>
      {children}
    </div>
  );
}

// ============================================
// DROP ZONE COMPONENT
// ============================================
function DropZone({ id, children, label, isOver, className }) {
  const { setNodeRef } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className={`p-3 rounded-lg border-2 border-dashed min-h-[60px] transition-all ${className || ''} ${
        isOver ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-gray-50'
      }`}
    >
      {children || (
        <p className="text-xs text-center text-gray-400 py-2">{label || 'Drop here'}</p>
      )}
    </div>
  );
}

// ============================================
// LINE ITEM HELPERS — a load must trace back to the line it serves.
// ============================================
// `fleet_allocations.line_item_id` is that link (line item → job → quote). It is what lets a
// per-line purchase order, the POD screens and the client's view say WHICH line a truck moved —
// without it a load created here is just a random load. Pure module-scope helpers so the data
// loader and the render always agree, with no stale-closure risk.

// 'L1 of 2' — stored on the load as `leg_label`, so it still reads correctly later.
const legLabelFor = (lines, lineItemId) => {
  const i = (lines || []).findIndex((l) => l.id === lineItemId);
  if (i === -1) return null;
  return lines.length > 1 ? `L${i + 1} of ${lines.length}` : 'L1';
};

// 'collect 21 Sep → deliver 23 Sep · 1 load' — the window this line has to be moved in.
const lineWindow = (line) => {
  if (!line) return '';
  const day = (v) => {
    if (!v) return null;
    const d = new Date(`${v}T00:00:00Z`);
    return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' });
  };
  const from = day(line.pickup_date);
  const to = day(line.delivery_date);
  const parts = [];
  if (from || to) parts.push(`collect ${from || '?'} → deliver ${to || '?'}`);
  if (line.quantity) parts.push(`${line.quantity} ${line.unit || ''}`.trim());
  return parts.join(' · ');
};

// ---- What to call a unit on screen ----
// One shared helper (see src/utils/unitLabels.js): the subbie's own NAME for the truck leads
// ("H94"), with the registration underneath. It lives in utils/ because three separate screens had
// the same bug — each labelling a truck with `${make} ${model}`, which is blank for a fleet that
// is named rather than described.
// ============================================
// ============================================
// SHELL — the SAME scheduler serves both portals.
// A subbie sees it inside their own layout. When ONE PURPOSE STAFF schedule on a subbie's
// behalf (admin mode: ?admin=true&subbieId=…) they must see the ADMIN layout, otherwise they
// would be looking at subcontractor navigation. Only the chrome differs — the screen, the
// drag-and-drop grid and the save calls are byte-for-byte the same for both, which is exactly
// what "the admin schedules the same way the subbie does" requires.
// ============================================
function SchedulerShell({ isAdminMode, onLogout, user, children }) {
  if (isAdminMode) {
    return <AdminLayout onLogout={onLogout}>{children}</AdminLayout>;
  }
  return (
    <SubbieLayout activeTab="activejobs" onLogout={onLogout} user={user}>
      {children}
    </SubbieLayout>
  );
}

// MAIN COMPONENT
// ============================================
function SubbieScheduler({ onLogout }) {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [job, setJob] = useState(null);
  const [user, setUser] = useState({});
  const [subbieId, setSubbieId] = useState(null);
  const [availableTractors, setAvailableTractors] = useState([]);
  const [availableTrailers, setAvailableTrailers] = useState([]);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [availableCranes, setAvailableCranes] = useState([]);
  const [rows, setRows] = useState([]);
  const [jobProgress, setJobProgress] = useState({ total: 0, delivered: 0, percentage: 0, remaining: 0 });
  const [submitting, setSubmitting] = useState(false);
  const [jobDateRange, setJobDateRange] = useState({ start: null, end: null });
  const [activeId, setActiveId] = useState(null);
  const [activeItem, setActiveItem] = useState(null);
  const [allocatedUnits, setAllocatedUnits] = useState([]);
  const [activeAllocations, setActiveAllocations] = useState([]);
  const [completedAllocations, setCompletedAllocations] = useState([]);
  const [showWeighbridgeModal, setShowWeighbridgeModal] = useState(false);
  const [showPODModal, setShowPODModal] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [uploadData, setUploadData] = useState({
    volume: '',
    weight: '',
    pod_number: '',
    receiver_name: '',
    image_url: ''
  });
  
  // ✅ Admin Mode State
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [adminSubbieId, setAdminSubbieId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    
    // ✅ Check if admin mode
    const params = new URLSearchParams(location.search);
    const admin = params.get('admin');
    const adminSubbieIdParam = params.get('subbieId');
    
    if (admin === 'true' && adminSubbieIdParam) {
      setIsAdminMode(true);
      setAdminSubbieId(adminSubbieIdParam);
      setSubbieId(adminSubbieIdParam);
      fetchData(adminSubbieIdParam);
    } else {
      getSubbieId(userData);
    }
  }, [jobId, location.search]);

  const getSubbieId = async (userData) => {
    try {
      let savedId = localStorage.getItem('subbieId');
      if (savedId) {
        setSubbieId(savedId);
        fetchData(savedId);
        return;
      }

      const response = await fetch(`${API_URL}/subcontractors?user_id=${userData.id}`);
      const data = await response.json();
      if (data.success && data.subcontractors && data.subcontractors.length > 0) {
        const id = data.subcontractors[0].id;
        setSubbieId(id);
        localStorage.setItem('subbieId', id);
        fetchData(id);
      } else {
        setLoading(false);
        toast.error('No subcontractor profile found');
      }
    } catch (err) {
      console.error('Error fetching subbie ID:', err);
      toast.error('Failed to load profile');
      setLoading(false);
    }
  };

  const fetchData = async (id) => {
    setLoading(true);
    setError(null);
    try {
      console.log('🔍 Fetching job:', jobId);
      const jobRes = await fetch(`${API_URL}/subbie/job/${jobId}?subbie_id=${id}`);
      const jobData = await jobRes.json();
      if (!jobData.success) {
        throw new Error('Job not found');
      }
      setJob(jobData.job);
      const startDate = jobData.job?.start_date || new Date().toISOString().split('T')[0];
      const endDate = jobData.job?.estimated_end_date || new Date(Date.now() + 7*24*60*60*1000).toISOString().split('T')[0];
      setJobDateRange({ start: startDate, end: endDate });

      console.log('🔍 Fetching fleet availability');
      const fleetRes = await fetch(
        `${API_URL}/fleet/availability/${id}?start_date=${startDate}&end_date=${endDate}`
      );
      const fleetData = await fleetRes.json();
      if (fleetData.success) {
        setAvailableTractors(fleetData.trucks || []);
        setAvailableTrailers(fleetData.trailers || []);
        setAvailableDrivers(fleetData.drivers || []);
        setAvailableCranes(fleetData.cranes || []);
      }

      console.log('🔍 Fetching allocations for job:', jobId);
      const allocRes = await fetch(`${API_URL}/fleet/fleet-allocations?job_id=${jobId}`);
      const allocData = await allocRes.json();
      
      console.log('📦 Alloc data:', allocData);

      if (allocData.success) {
        const everyAllocation = allocData.allocations || allocData.data || [];

        // ONLY this subbie's loads. /fleet/fleet-allocations returns EVERY allocation on the job,
        // and an admin's RLS is permissive (is_staff()), so in admin mode the grid would otherwise
        // load — and let you drag, re-save or remove — another subcontractor's trucks. For a
        // subbie this filter changes nothing: RLS already limits them to their own rows.
        const allAllocations = id
          ? everyAllocation.filter(a => a.subcontractor_id === id)
          : everyAllocation;
        console.log('📦 Found allocations:', allAllocations.length);
        
        const active = allAllocations.filter(a => 
          a.status !== 'completed' && 
          a.status !== 'delivered'
        );
        
        const completed = allAllocations.filter(a => 
          a.status === 'completed' || a.status === 'delivered'
        );
        
        console.log('📦 Active allocations:', active.length);
        console.log('📦 Completed allocations:', completed.length);
        
        setActiveAllocations(active);
        setCompletedAllocations(completed);
        setAllocatedUnits(allAllocations);
        
        let deliveredMT = 0;
        allAllocations.forEach(unit => {
          if (unit.status === 'delivered' || unit.status === 'completed') {
            const weight = unit.weighbridge_weight_mt || 0;
            deliveredMT += parseFloat(weight) || 0;
          }
        });
        const totalMT = jobData.job?.total_quantity_mt || 0;
        setJobProgress({
          total: totalMT,
          delivered: deliveredMT,
          percentage: totalMT > 0 ? Math.round((deliveredMT / totalMT) * 100) : 0,
          remaining: Math.max(0, totalMT - deliveredMT)
        });

        // ---- Rows are grouped by the LINE ITEM each truck serves ----
        // `line_item_id` is the link line item → job → quote. Without it a load created here is
        // just a random load: a per-line purchase order cannot be built from it, the POD screens
        // print "Load details not linked", and nothing can say which line was completed.
        const lines = [...((jobData.job && jobData.job.line_items) || [])]
          .sort((a, b) => (Number(a.line_number) || 0) - (Number(b.line_number) || 0));
        let seq = 0;
        const blankRow = (lineItemId) => {
          const line = lines.find((l) => l.id === lineItemId) || null;
          return {
            id: `row-${++seq}`,
            allocationId: null,
            lineItemId: lineItemId || null,
            lineLabel: legLabelFor(lines, lineItemId),
            tractor: null,
            trailer1: null,
            trailer2: null,
            driverId: null,
            // Pre-filled with THIS LINE'S collection date (still editable). Before, every row
            // defaulted to the job's start date, so the 23 Sep leg showed 21 Sep — the one date
            // that actually distinguishes the two lines was wrong.
            arrivalDate: (line && line.pickup_date) || startDate,
            status: 'pending'
          };
        };
        const fromAllocation = (unit) => ({
          id: `row-${++seq}`,
          allocationId: unit.id,
          lineItemId: unit.line_item_id || null,
          lineLabel: legLabelFor(lines, unit.line_item_id),
          tractor: unit.truck || null,
          trailer1: unit.trailer_1 || null,
          trailer2: unit.trailer_2 || null,
          driverId: unit.driver_id || null,
          arrivalDate: unit.arrival_date || startDate,
          status: unit.status || 'pending'
        });

        let initialRows = [];
        if (lines.length > 0) {
          // One group per line, in line order: its existing loads, or one empty truck slot so the
          // line can be allocated straight away. More trucks can be added to any line.
          lines.forEach((line) => {
            const onThisLine = active.filter((a) => a.line_item_id === line.id);
            if (onThisLine.length > 0) initialRows = initialRows.concat(onThisLine.map(fromAllocation));
            else initialRows.push(blankRow(line.id));
          });
          // Loads with no line item — created before this link existed, or raised off-system — must
          // not vanish. They get their own group at the bottom so they are visible and can be
          // re-linked by moving the truck onto the right line.
          const knownIds = new Set(lines.map((l) => l.id));
          const unlinked = active.filter((a) => !a.line_item_id || !knownIds.has(a.line_item_id));
          initialRows = initialRows.concat(unlinked.map(fromAllocation));
        } else if (active.length > 0) {
          // A job with no line items at all: previous behaviour, simply linked to nothing.
          initialRows = active.map(fromAllocation);
        } else {
          const trucksRequired = (jobData.job && jobData.job.trucks_required) || 2;
          for (let i = 0; i < trucksRequired; i++) initialRows.push(blankRow(null));
        }
        setRows(initialRows);
        console.log('📦 Rows set:', initialRows.length);
      }

    } catch (err) {
      console.error('❌ Error fetching data:', err);
      setError(err.message || 'Failed to load scheduler');
      toast.error('Failed to load scheduler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // DRAG AND DROP HANDLERS
  // ============================================
  const getAllAssignedIds = () => {
    const assigned = {};
    rows.forEach(row => {
      if (row.tractor) assigned[row.tractor.id] = 'tractor';
      if (row.trailer1) assigned[row.trailer1.id] = 'trailer';
      if (row.trailer2) assigned[row.trailer2.id] = 'trailer';
    });
    return assigned;
  };

  const getAssignedDrivers = () => {
    return rows.map(row => row.driverId).filter(id => id !== null);
  };

  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
    
    // Tag what is being dragged. The overlay used to guess from `make`, which is NULL on plenty of
    // real trucks (H94 among them) — so dragging a tractor drew a TRAILER card.
    const allItems = [
      ...availableTractors.map((t) => ({ ...t, _kind: 'tractor' })),
      ...availableTrailers.map((t) => ({ ...t, _kind: 'trailer' }))
    ];
    const item = allItems.find(i => i.id === active.id);
    if (item) {
      setActiveItem(item);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveItem(null);

    if (!over) return;
    if (active.id === over.id) return;

    const overId = over.id;
    const activeId = active.id;

    if (overId.includes('row-')) {
      const parts = overId.split('-');
      const rowIndex = parseInt(parts[1]) - 1;
      const slotType = parts.slice(2).join('-');

      if (isNaN(rowIndex)) return;
      if (rowIndex < 0 || rowIndex >= rows.length) return;

      const row = rows[rowIndex];
      const allItems = [...availableTractors, ...availableTrailers];
      const item = allItems.find(i => i.id === activeId);
      if (!item) return;

      const isTractor = availableTractors.some(t => t.id === activeId);
      const isTrailer = availableTrailers.some(t => t.id === activeId);

      if (isTractor && slotType !== 'tractor') return;
      if (isTrailer && !slotType.includes('trailer')) return;

      const assigned = getAllAssignedIds();
      
      if (assigned[activeId]) {
        removeItemFromRow(activeId);
      }

      if (isTrailer) {
        const trailerCount = [row.trailer1, row.trailer2].filter(t => t !== null).length;
        if (trailerCount >= 2) {
          toast('Maximum 2 trailers per tractor');
          return;
        }
        if (!row.trailer1) {
          updateRow(rowIndex, 'trailer1', item);
        } else if (!row.trailer2) {
          updateRow(rowIndex, 'trailer2', item);
        } else {
          toast('Both trailer slots are full');
          return;
        }
      } else if (isTractor) {
        const existingRowIndex = rows.findIndex((r, idx) => 
          idx !== rowIndex && r.tractor && r.tractor.id === activeId
        );
        
        if (existingRowIndex !== -1) {
          const existingRow = rows[existingRowIndex];
          setAvailableTractors(prev => [...prev, existingRow.tractor]);
          updateRow(existingRowIndex, 'tractor', null);
          toast.info(`Moved ${unitLabel(item) || 'unit'} from Row ${existingRowIndex + 1} to Row ${rowIndex + 1}`);
        }
        
        if (row.tractor) {
          setAvailableTractors(prev => [...prev, row.tractor]);
        }
        updateRow(rowIndex, 'tractor', item);
      }

      if (isTractor) {
        setAvailableTractors(prev => prev.filter(t => t.id !== activeId));
      } else if (isTrailer) {
        setAvailableTrailers(prev => prev.filter(t => t.id !== activeId));
      }
    }
  };

  const removeItemFromRow = (itemId) => {
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (row.tractor && row.tractor.id === itemId) {
        setAvailableTractors(prev => [...prev, row.tractor]);
        updateRow(i, 'tractor', null);
        return;
      }
      if (row.trailer1 && row.trailer1.id === itemId) {
        setAvailableTrailers(prev => [...prev, row.trailer1]);
        updateRow(i, 'trailer1', null);
        return;
      }
      if (row.trailer2 && row.trailer2.id === itemId) {
        setAvailableTrailers(prev => [...prev, row.trailer2]);
        updateRow(i, 'trailer2', null);
        return;
      }
    }
  };

  const updateRow = (index, field, value) => {
    const newRows = [...rows];
    newRows[index] = { ...newRows[index], [field]: value };
    setRows(newRows);
  };

  const removeItemFromRowByIndex = (rowIndex, field) => {
    const row = rows[rowIndex];
    const item = row[field];
    if (!item) return;

    if (field === 'tractor') {
      setAvailableTractors(prev => [...prev, item]);
    } else if (field === 'trailer1' || field === 'trailer2') {
      setAvailableTrailers(prev => [...prev, item]);
    }
    updateRow(rowIndex, field, null);
  };

  const handleDriverSelect = (rowIndex, driverId) => {
    if (driverId) {
      const assignedDrivers = getAssignedDrivers();
      if (assignedDrivers.includes(driverId)) {
        toast('This driver is already assigned to another row');
        return;
      }
    }
    
    const newRows = [...rows];
    newRows[rowIndex] = { ...newRows[rowIndex], driverId };
    setRows(newRows);
  };

  const handleArrivalDateChange = (rowIndex, date) => {
    const newRows = [...rows];
    newRows[rowIndex] = { ...newRows[rowIndex], arrivalDate: date };
    setRows(newRows);
  };

  // Add a truck to a specific line item. `lineItemId` is what ties the saved load to its line.
  const addRow = (lineItemId = null) => {
    const jobLines = [...((job && job.line_items) || [])]
      .sort((a, b) => (Number(a.line_number) || 0) - (Number(b.line_number) || 0));
    const newRow = {
      id: `row-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      allocationId: null,
      lineItemId: lineItemId || null,
      lineLabel: legLabelFor(jobLines, lineItemId),
      tractor: null,
      trailer1: null,
      trailer2: null,
      driverId: null,
      arrivalDate: jobDateRange.start || new Date().toISOString().split('T')[0],
      status: 'pending'
    };

    // Rows must stay CONTIGUOUS BY LINE — the grid draws a line's header once, when the line
    // changes, so a row appended at the very bottom would show up under whichever line is last.
    const newRows = [...rows];
    let insertAt = newRows.length;
    if (lineItemId) {
      const lineOrder = jobLines.map((l) => l.id);
      const pos = lineOrder.indexOf(lineItemId);
      // directly after that line's last row…
      for (let i = newRows.length - 1; i >= 0; i--) {
        if (newRows[i].lineItemId === lineItemId) { insertAt = i + 1; break; }
      }
      // …or, if it has no rows left, before the first line that comes after it
      if (insertAt === newRows.length && pos >= 0) {
        for (let i = 0; i < newRows.length; i++) {
          if (lineOrder.indexOf(newRows[i].lineItemId) > pos) { insertAt = i; break; }
        }
      }
    }
    newRows.splice(insertAt, 0, newRow);
    setRows(newRows);
  };

  const removeRow = (index) => {
    const row = rows[index];
    if (row.tractor) setAvailableTractors(prev => [...prev, row.tractor]);
    if (row.trailer1) setAvailableTrailers(prev => [...prev, row.trailer1]);
    if (row.trailer2) setAvailableTrailers(prev => [...prev, row.trailer2]);

    const jobLines = [...((job && job.line_items) || [])]
      .sort((a, b) => (Number(a.line_number) || 0) - (Number(b.line_number) || 0));

    // A LINE MUST STAY ON SCREEN. If this was its last row, leave an empty slot behind rather than
    // removing it — otherwise dropping the truck you are still waiting on made the whole line
    // vanish, and you had nothing left to come back and allocate later. (The line reappeared only
    // after a reload, which is not good enough.) Extra trucks on a line still delete normally.
    const lastRowForItsLine = jobLines.length > 0
      && !!row.lineItemId
      && !rows.some((r, i) => i !== index && r.lineItemId === row.lineItemId);

    if (lastRowForItsLine) {
      const line = jobLines.find((l) => l.id === row.lineItemId) || null;
      const newRows = [...rows];
      newRows[index] = {
        id: `row-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        allocationId: null,
        lineItemId: row.lineItemId,
        lineLabel: legLabelFor(jobLines, row.lineItemId),
        tractor: null,
        trailer1: null,
        trailer2: null,
        driverId: null,
        arrivalDate: (line && line.pickup_date) || jobDateRange.start || new Date().toISOString().split('T')[0],
        status: 'pending'
      };
      setRows(newRows);
      return;
    }

    setRows(rows.filter((_, i) => i !== index));
  };

  // ============================================
  // SUBMIT ALLOCATIONS
  // ============================================
  const handleSubmit = async () => {
    // A line with NO truck on it is a line you are deliberately NOT scheduling yet — leave it
    // alone and save the rest. That is how you allocate L1 today and L2 tomorrow or the day after:
    // L2 stays on screen with an empty slot, and saving simply creates no load for it. (This used
    // to be impossible — the save insisted on a tractor on every row, so a half-scheduled job
    // could not be saved at all.)
    const deferredLines = rows.filter((row) => !row.tractor && row.lineItemId);
    const toSave = rows.filter((row) => row.tractor);

    if (toSave.length === 0) {
      toast('Nothing to save yet — drag a truck onto a line first');
      return;
    }

    // A row that HAS a tractor must be complete; a half-filled row is a mistake, not a deferral.
    const incomplete = toSave.filter((row) => !row.driverId || !row.arrivalDate);
    if (incomplete.length > 0) {
      const which = incomplete.map((row) => row.lineLabel || 'a line').join(', ');
      toast(`Add a driver and an arrival date for ${which}`);
      return;
    }

    setSubmitting(true);
    try {
      let allocatedCount = 0;
      for (const row of toSave) {
        const payload = {
          job_id: jobId,
          subcontractor_id: subbieId,
          truck_id: row.tractor.id,
          trailer_1_id: row.trailer1?.id || null,
          trailer_2_id: row.trailer2?.id || null,
          driver_id: row.driverId,
          start_date: jobDateRange.start,
          end_date: jobDateRange.end,
          arrival_date: row.arrivalDate,
          // THE LINE THIS LOAD SERVES. Without it the load cannot be traced back to a line item,
          // so a per-line purchase order, the POD screens and the client's view have nothing to
          // link to and every load looks random.
          line_item_id: row.lineItemId || null,
          leg_label: row.lineLabel || null
        };

        console.log('📦 Sending payload:', payload);

        let response;
        if (row.allocationId) {
          const updatePayload = {
            ...payload,
            status: row.status || 'pending'
          };
          console.log('📦 Updating allocation:', row.allocationId, updatePayload);
          response = await fetch(`${API_URL}/fleet/fleet-allocations/${row.allocationId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updatePayload)
          });
        } else {
          response = await fetch(`${API_URL}/fleet/allocate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
        }

        const data = await response.json();
        if (!data.success) {
          throw new Error(data.error || 'Failed to allocate');
        }
        allocatedCount++;
      }

      const stillToCome = deferredLines.length;
      toast.success(
        `✅ Fleet allocated successfully! ${allocatedCount} truck(s) allocated.` +
        (stillToCome > 0
          ? ` ${stillToCome} line(s) left unscheduled — they are still here to allocate whenever you are ready.`
          : '')
      );
      // Back to where this was opened from. An admin must NEVER be sent to /subbie-active-jobs:
      // that route is subbie-only, so ProtectedRoute would bounce them to the dashboard the moment
      // the save succeeded. Returning to the chosen subbie is also the right place to land.
      navigate(
        isAdminMode
          ? (adminSubbieId ? `/admin-scheduler?subbie=${adminSubbieId}` : '/admin-scheduler')
          : '/subbie-active-jobs'
      );
    } catch (err) {
      console.error('Error allocating fleet:', err);
      toast.error('Failed to allocate fleet: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'pending': 'bg-gray-100 text-gray-600',
      'on_route_to_loading': 'bg-indigo-100 text-indigo-600',
      'onsite': 'bg-blue-100 text-blue-600',
      'loading': 'bg-yellow-100 text-yellow-600',
      'en_route': 'bg-purple-100 text-purple-600',
      'at_destination': 'bg-orange-100 text-orange-600',
      'delivered': 'bg-yellow-100 text-yellow-700',
      'completed': 'bg-gray-200 text-gray-500'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-600'}`;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Pending',
      'on_route_to_loading': 'On Route to Load',
      'onsite': '📍 On Site',
      'loading': '🔄 Loading',
      'en_route': '🚛 En Route',
      'at_destination': '🏁 At Destination',
      'delivered': '✅ Delivered (Pending Approval)',
      'completed': '✅ Completed'
    };
    return labels[status] || status;
  };

  const canUpdateStatus = (currentStatus, newStatus) => {
    const order = ['pending', 'on_route_to_loading', 'onsite', 'loading', 'en_route', 'at_destination', 'delivered', 'completed'];
    const currentIdx = order.indexOf(currentStatus);
    const newIdx = order.indexOf(newStatus);
    return newIdx === currentIdx + 1 || newIdx === currentIdx;
  };

  const handleUpdateStatus = async (unitId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/fleet/fleet-allocations/${unitId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Status updated to ${getStatusLabel(newStatus)}`);
        fetchData(subbieId);
      } else {
        toast.error(data.error || 'Failed to update status');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error('Failed to update status');
    }
  };

  const [removeUnitTarget, setRemoveUnitTarget] = useState(null); // unit id to remove

  const handleRemoveUnit = (unitId) => {
    setRemoveUnitTarget(unitId);
  };

  const performRemoveUnit = async () => {
    if (!removeUnitTarget) return;
    try {
      const response = await fetch(`${API_URL}/fleet/fleet-allocations/${removeUnitTarget}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Unit removed successfully');
        fetchData(subbieId);
      } else {
        toast.error(data.error || 'Failed to remove unit');
      }
    } catch (err) {
      console.error('Error removing unit:', err);
      toast.error('Failed to remove unit');
    }
    setRemoveUnitTarget(null);
  };

  const handleWeighbridgeUpload = async (unitId) => {
    if (!uploadData.volume || !uploadData.weight) {
      toast.error('Please enter volume and weight');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/fleet/fleet-allocations/${unitId}/weighbridge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          weighbridge_volume_mt: parseFloat(uploadData.volume),
          weighbridge_weight_mt: parseFloat(uploadData.weight),
          weighbridge_image_url: uploadData.image_url || null
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('✅ Weighbridge ticket uploaded!');
        setShowWeighbridgeModal(false);
        setUploadData({ volume: '', weight: '', pod_number: '', receiver_name: '', image_url: '' });
        fetchData(subbieId);
      } else {
        toast.error(data.error || 'Failed to upload');
      }
    } catch (err) {
      console.error('Error uploading weighbridge:', err);
      toast.error('Failed to upload');
    }
  };

  const handlePODUpload = async (unitId) => {
    if (!uploadData.pod_number || !uploadData.receiver_name) {
      toast.error('Please enter POD number and receiver name');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/fleet/fleet-allocations/${unitId}/pod`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pod_number: uploadData.pod_number,
          pod_receiver_name: uploadData.receiver_name,
          pod_image_url: uploadData.image_url || null,
          pod_status: 'pending'
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('✅ POD uploaded! Waiting for admin approval.');
        setShowPODModal(false);
        setUploadData({ volume: '', weight: '', pod_number: '', receiver_name: '', image_url: '' });
        fetchData(subbieId);
      } else {
        toast.error(data.error || 'Failed to upload POD');
      }
    } catch (err) {
      console.error('Error uploading POD:', err);
      toast.error('Failed to upload POD');
    }
  };

  const remainingMT = jobProgress.remaining || 0;

  // Grouping data for the grid. Rows are ordered by line (the loader guarantees it), so the group
  // header and the per-line truck number are derived here rather than tracked in state.
  const jobLines = [...((job && job.line_items) || [])]
    .sort((a, b) => (Number(a.line_number) || 0) - (Number(b.line_number) || 0));
  const jobLineById = new Map(jobLines.map((l) => [l.id, l]));
  // A row with no tractor is a line you have deliberately not scheduled YET — they stay on screen
  // with an empty slot so you can come back to them, so count the trucks actually scheduled.
  const scheduledCount = rows.filter((r) => r.tractor).length;
  const deferredCount = rows.filter((r) => !r.tractor && r.lineItemId).length;
  const rowMeta = (() => {
    const out = [];
    let prev = undefined;
    let pos = 0;
    rows.forEach((row, i) => {
      const key = row.lineItemId || null;
      const starts = key !== prev;
      pos = starts ? 1 : pos + 1;
      const nextKey = i + 1 < rows.length ? (rows[i + 1].lineItemId || null) : undefined;
      out.push({ starts, ends: key !== nextKey, pos });
      prev = key;
    });
    return out;
  })();

  if (loading) {
    return (
      <SchedulerShell isAdminMode={isAdminMode} onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <FaSpinner className="animate-spin text-4xl text-green-500 mx-auto mb-4" />
            <p className="text-gray-500">Loading scheduler...</p>
          </div>
        </div>
      </SchedulerShell>
    );
  }

  if (!job) {
    return (
      <SchedulerShell isAdminMode={isAdminMode} onLogout={onLogout} user={user}>
        <div className="bg-white rounded-xl p-8 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-red-600 mb-2">Job Not Found</h2>
          <p className="text-gray-500">The job you're looking for doesn't exist.</p>
          <Link to={isAdminMode ? '/admin-scheduler' : '/subbie-active-jobs'} className="mt-4 inline-block px-4 py-2 rounded-lg text-white" style={{ backgroundColor: '#00A86B' }}>
            {isAdminMode ? 'Back to Subbie Scheduling' : 'Back to Active Jobs'}
          </Link>
        </div>
      </SchedulerShell>
    );
  }

  return (
    <SchedulerShell isAdminMode={isAdminMode} onLogout={onLogout} user={user}>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to={isAdminMode ? '/admin-scheduler' : '/subbie-active-jobs'} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" style={{ color: '#0A1929' }}>
          <FaArrowLeft size={20} />
        </Link>
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>
            🚛 Job Scheduler
          </h1>
          <p className="text-sm" style={{ color: '#4A5568' }}>
            {job.job_number} - {job.commodity} | {job.total_quantity_mt} MT | {job.origin} → {job.destination}
          </p>
        </div>
      </div>

      {/* ✅ Admin Mode Banner */}
      {isAdminMode && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 flex items-center gap-3">
          <FaInfoCircle className="text-blue-600" size={20} />
          <div>
            <p className="text-sm font-medium text-blue-800">🔧 Admin Mode: Allocating Fleet for Subbie</p>
            <p className="text-xs text-blue-600">You are allocating fleet on behalf of the subcontractor. They will be able to see and modify these allocations.</p>
          </div>
        </div>
      )}

      {/* Progress Bar */}
      {jobProgress.total > 0 && (
        <div className="bg-white rounded-xl border p-6 mb-6" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm font-medium" style={{ color: '#0A1929' }}>Job Progress</span>
            <span className="text-sm font-bold" style={{ color: '#00A86B' }}>{jobProgress.percentage}%</span>
          </div>
          <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(jobProgress.percentage, 100)}%`, backgroundColor: jobProgress.percentage >= 100 ? '#047857' : '#00A86B' }} />
          </div>
          <div className="flex justify-between mt-2 text-xs text-gray-500">
            <span>{jobProgress.delivered.toFixed(1)} MT delivered</span>
            <span>{jobProgress.total.toFixed(1)} MT total</span>
            {remainingMT > 0 && <span className="text-orange-500">{remainingMT.toFixed(1)} MT remaining</span>}
            {remainingMT === 0 && jobProgress.total > 0 && <span className="text-green-600">🎉 Fully delivered!</span>}
          </div>
        </div>
      )}

      {/* Completed Units Section */}
      {completedAllocations.length > 0 && (
        <div className="bg-green-50 rounded-xl border border-green-200 p-4 mb-6">
          <div className="flex items-center gap-2 text-sm font-medium text-green-700 mb-2">
            <FaHistory size={16} /> Completed Trucks ({completedAllocations.length})
          </div>
          <div className="flex flex-wrap gap-2">
            {completedAllocations.map((unit) => (
              <span key={unit.id} className="px-3 py-1 bg-green-100 text-green-700 rounded-lg text-xs flex items-center gap-1">
                <FaCheckCircle size={12} />
                {unitLabel(unit.truck) || 'Unknown'}
                <span className="text-gray-400 ml-1">({unit.status})</span>
              </span>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">These trucks have completed their deliveries and are available for re-allocation.</p>
        </div>
      )}

      <DndContext 
        sensors={sensors} 
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {/* Available Fleet */}
        <div className="bg-white rounded-xl border p-6 mb-6" style={{ borderColor: '#E8ECEF' }}>
          <h3 className="font-bold mb-4 flex items-center gap-2" style={{ color: '#0A1929' }}>
            📦 Available Fleet
            <span className="ml-auto text-xs" style={{ color: '#4A5568' }}>
              Date: {jobDateRange.start} → {jobDateRange.end}
            </span>
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Tractors */}
            <div>
              <p className="text-sm font-medium mb-2" style={{ color: '#0A1929' }}>
                🚛 Tractors ({availableTractors.length})
              </p>
              <SortableContext items={availableTractors.map(t => t.id)} strategy={verticalListSortingStrategy}>
                <div className="flex flex-wrap gap-2">
                  {availableTractors.length === 0 ? (
                    <p className="text-xs text-gray-400">No tractors available</p>
                  ) : (
                    availableTractors.map((tractor) => (
                      <SortableItem key={tractor.id} id={tractor.id} isAvailable={tractor.is_available}>
                        <div className={`p-3 rounded-lg border-2 hover:shadow-md transition-shadow min-w-[140px] ${
                          tractor.is_available 
                            ? 'bg-blue-50 border-blue-200 cursor-grab' 
                            : 'bg-gray-200 border-gray-300 opacity-50 cursor-not-allowed'
                        }`}>
                          <FaTruck className={`mb-1 ${tractor.is_available ? 'text-blue-600' : 'text-gray-500'}`} />
                          <p className="font-bold text-sm text-gray-800">{unitLabel(tractor) || 'Tractor'}</p>
                          {unitSubLabel(tractor) && <p className="text-xs text-gray-500">{unitSubLabel(tractor)}</p>}
                          <p className={`text-xs font-medium ${tractor.is_available ? 'text-green-600' : 'text-red-500'}`}>
                            {tractor.is_available ? '✅ Available' : '❌ Unavailable'}
                          </p>
                          {tractor.busy_on_other_loads && (
                            <p className='text-xs font-medium text-amber-600 mt-0.5'>⚠️ Busy on another load</p>
                          )}
                        </div>
                      </SortableItem>
                    ))
                  )}
                </div>
              </SortableContext>
            </div>

            {/* Trailers */}
            <div>
              <p className="text-sm font-medium mb-2" style={{ color: '#0A1929' }}>
                📦 Trailers ({availableTrailers.length})
              </p>
              <SortableContext items={availableTrailers.map(t => t.id)} strategy={verticalListSortingStrategy}>
                <div className="flex flex-wrap gap-2">
                  {availableTrailers.length === 0 ? (
                    <p className="text-xs text-gray-400">No trailers available</p>
                  ) : (
                    availableTrailers.map((trailer) => (
                      <SortableItem key={trailer.id} id={trailer.id} isAvailable={trailer.is_available}>
                        <div className={`p-3 rounded-lg border-2 hover:shadow-md transition-shadow min-w-[140px] ${
                          trailer.is_available 
                            ? 'bg-purple-50 border-purple-200 cursor-grab' 
                            : 'bg-gray-200 border-gray-300 opacity-50 cursor-not-allowed'
                        }`}>
                          <FaTrailer className={`mb-1 ${trailer.is_available ? 'text-purple-600' : 'text-gray-500'}`} />
                          <p className="font-bold text-sm text-gray-800">{trailer.trailer_type}</p>
                          <p className="text-xs text-gray-500">{trailer.registration_number}</p>
                          <p className={`text-xs font-medium ${trailer.is_available ? 'text-green-600' : 'text-red-500'}`}>
                            {trailer.is_available ? '✅ Available' : '❌ Unavailable'}
                          </p>
                          {trailer.busy_on_other_loads && (
                            <p className='text-xs font-medium text-amber-600 mt-0.5'>⚠️ Busy on another load</p>
                          )}
                        </div>
                      </SortableItem>
                    ))
                  )}
                </div>
              </SortableContext>
            </div>

            {/* Drivers */}
            <div>
              <p className="text-sm font-medium mb-2" style={{ color: '#0A1929' }}>
                👤 Drivers ({availableDrivers.length})
              </p>
              <div className="flex flex-wrap gap-2">
                {availableDrivers.length === 0 ? (
                  <p className="text-xs text-gray-400">No drivers available</p>
                ) : (
                  availableDrivers.map((driver) => (
                    <div key={driver.id} className={`p-3 rounded-lg border-2 min-w-[140px] ${
                      driver.is_available 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-gray-200 border-gray-300 opacity-50'
                    }`}>
                      <FaUser className={`mb-1 ${driver.is_available ? 'text-green-600' : 'text-gray-500'}`} />
                      <p className="font-bold text-sm text-gray-800">{driver.full_name}</p>
                      <p className="text-xs text-gray-500">{driver.phone}</p>
                      <p className={`text-xs font-medium ${driver.is_available ? 'text-green-600' : 'text-red-500'}`}>
                        {driver.is_available ? '✅ Available' : '❌ Unavailable'}
                      </p>
                      {driver.busy_on_other_loads && (
                        <p className='text-xs font-medium text-amber-600 mt-0.5'>⚠️ Busy on another load</p>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Cranes */}
            <div>
              <p className='text-sm font-medium mb-2' style={{ color: '#0A1929' }}>
                🏗️ Cranes ({availableCranes.length})
              </p>
              <div className='flex flex-wrap gap-2'>
                {availableCranes.length === 0 ? (
                  <p className='text-xs text-gray-400'>No cranes available</p>
                ) : (
                  availableCranes.map((crane) => (
                    <div key={crane.id} className={`p-3 rounded-lg border-2 min-w-[140px] ${crane.busy_on_other_loads ? 'bg-amber-50 border-amber-200' : 'bg-orange-50 border-orange-200'}`}>
                      <p className='text-lg mb-1'>🏗️</p>
                      <p className='font-bold text-sm text-gray-800'>{crane.unit_name || `${crane.make || ''} ${crane.model || ''}`.trim() || 'Crane'}</p>
                      <p className='text-xs text-gray-500'>{crane.crane_type ? crane.crane_type.replace(/_/g, ' ') : ''}</p>
                      <p className='text-xs font-medium text-green-600'>✅ Available</p>
                      {crane.busy_on_other_loads && (
                        <p className='text-xs font-medium text-amber-600 mt-0.5'>⚠️ Busy on another load</p>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Allocation Rows */}
        <div className="bg-white rounded-xl border p-6 mb-6" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold" style={{ color: '#0A1929' }}>
              🎯 Active Allocations ({scheduledCount} truck{scheduledCount === 1 ? '' : 's'} scheduled
              {jobLines.length > 0 ? ` · ${jobLines.length} line${jobLines.length === 1 ? '' : 's'}` : ''}
              {deferredCount > 0 ? ` · ${deferredCount} still to schedule` : ''})
            </h3>
            {/* When the job HAS line items, each line carries its own "add another truck" button,
                so a single global Add Row would only create a load belonging to no line at all. */}
            {jobLines.length === 0 && remainingMT > 0 && (
              <button
                type="button"
                onClick={() => addRow()}
                className="flex items-center gap-1 text-sm px-3 py-1 rounded-lg text-white transition-colors"
                style={{ backgroundColor: '#00A86B' }}
              >
                <FaPlus size={12} /> Add Row
              </button>
            )}
          </div>

          {rows.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No active allocations</p>
            </div>
          ) : (
            <div className="space-y-3">
              {rows.map((row, index) => {
                const hasTractor = row.tractor !== null;
                const trailerCount = [row.trailer1, row.trailer2].filter(t => t !== null).length;
                const meta = rowMeta[index] || {};
                const line = jobLineById.get(row.lineItemId) || null;

                return (
                  <React.Fragment key={row.id}>
                    {/* Which LINE this truck is being allocated for. Everything below belongs to it. */}
                    {meta.starts && jobLines.length > 0 && (
                      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 rounded-lg"
                        style={{ backgroundColor: '#F0FDF9', border: '1px solid #D1FAE5' }}>
                        <span className="text-xs font-bold" style={{ color: '#0A1929' }}>
                          {legLabelFor(jobLines, row.lineItemId) || 'Line not linked'}
                          {line && (line.title || line.commodity) ? ` · ${line.title || line.commodity}` : ''}
                        </span>
                        <span className="text-xs" style={{ color: '#64748B' }}>
                          {line ? (lineWindow(line) || 'no dates on this line')
                            : 'This load is not linked to a line item yet'}
                        </span>
                      </div>
                    )}
                  <div className="p-4 border rounded-lg" style={{ borderColor: '#E8ECEF' }}>
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-medium" style={{ color: '#0A1929' }}>
                        Truck {meta.pos || index + 1}{' '}
                        {hasTractor
                          ? '✅'
                          : <span className="text-xs font-normal" style={{ color: '#94A3B8' }}>— not scheduled yet</span>}
                        {row.status && row.status !== 'pending' && (
                          <span className="text-xs text-gray-400 ml-2">({row.status.replace('_', ' ')})</span>
                        )}
                      </span>
                      {row.allocationId ? (
                        <span className="text-xs text-gray-400">Existing allocation</span>
                      ) : (
                        <button onClick={() => removeRow(index)} className="text-red-400 hover:text-red-600" disabled={rows.length <= 1}>
                          <FaTimes size={16} />
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                      {/* Tractor Drop Zone */}
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">Tractor *</p>
                        <DropZone id={`${row.id}-tractor`} isOver={false} label="Drop Tractor Here">
                          {row.tractor ? (
                            <div className="flex items-center justify-between p-2 bg-blue-50 rounded-lg border border-blue-200">
                              <div className="flex items-center gap-2">
                                <FaTruck className="text-blue-600" />
                                <span className="text-sm font-medium">{unitLabel(row.tractor) || 'Tractor'}</span>
                                {unitSubLabel(row.tractor) && <span className="text-xs text-gray-500">{unitSubLabel(row.tractor)}</span>}
                              </div>
                              <button onClick={() => removeItemFromRowByIndex(index, 'tractor')} className="text-red-400 hover:text-red-600">
                                <FaTimes size={14} />
                              </button>
                            </div>
                          ) : (
                            <p className="text-xs text-center text-gray-400 py-3">Drop Tractor Here</p>
                          )}
                        </DropZone>
                      </div>

                      {/* Trailer 1 Drop Zone */}
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">Trailer 1</p>
                        <DropZone id={`${row.id}-trailer1`} isOver={false} label="Drop Trailer Here">
                          {row.trailer1 ? (
                            <div className="flex items-center justify-between p-2 bg-purple-50 rounded-lg border border-purple-200">
                              <div className="flex items-center gap-2">
                                <FaTrailer className="text-purple-600" />
                                <span className="text-sm font-medium">{row.trailer1.trailer_type}</span>
                                <span className="text-xs text-gray-500">{row.trailer1.registration_number}</span>
                              </div>
                              <button onClick={() => removeItemFromRowByIndex(index, 'trailer1')} className="text-red-400 hover:text-red-600">
                                <FaTimes size={14} />
                              </button>
                            </div>
                          ) : (
                            <p className="text-xs text-center text-gray-400 py-3">Drop Trailer Here</p>
                          )}
                        </DropZone>
                      </div>

                      {/* Trailer 2 Drop Zone */}
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">Trailer 2</p>
                        <DropZone id={`${row.id}-trailer2`} isOver={false} label="Drop Trailer Here">
                          {row.trailer2 ? (
                            <div className="flex items-center justify-between p-2 bg-purple-50 rounded-lg border border-purple-200">
                              <div className="flex items-center gap-2">
                                <FaTrailer className="text-purple-600" />
                                <span className="text-sm font-medium">{row.trailer2.trailer_type}</span>
                                <span className="text-xs text-gray-500">{row.trailer2.registration_number}</span>
                              </div>
                              <button onClick={() => removeItemFromRowByIndex(index, 'trailer2')} className="text-red-400 hover:text-red-600">
                                <FaTimes size={14} />
                              </button>
                            </div>
                          ) : (
                            <p className="text-xs text-center text-gray-400 py-3">Drop Trailer Here</p>
                          )}
                        </DropZone>
                        <p className="text-xs text-gray-400 mt-1">{trailerCount}/2 assigned</p>
                      </div>

                      {/* Driver Dropdown */}
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">Driver *</p>
                        <select
                          value={row.driverId || ''}
                          onChange={(e) => handleDriverSelect(index, e.target.value || null)}
                          className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:border-green ${!hasTractor ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                          style={{ borderColor: '#E8ECEF' }}
                          disabled={!hasTractor}
                        >
                          <option value="">Select Driver</option>
                          {availableDrivers
                            .filter(d => (d.is_available !== false) && (!getAssignedDrivers().includes(d.id) || d.id === row.driverId))
                            .map(driver => (
                              <option key={driver.id} value={driver.id}>{driver.full_name}</option>
                            ))}
                        </select>
                        {!hasTractor && <p className="text-xs text-red-400 mt-1">Assign tractor first</p>}
                      </div>

                      {/* Arrival Date Picker */}
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">Arrival Date *</p>
                        <input
                          type="date"
                          value={row.arrivalDate || jobDateRange.start || ''}
                          onChange={(e) => handleArrivalDateChange(index, e.target.value)}
                          className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:border-green ${!hasTractor ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                          style={{ borderColor: '#E8ECEF' }}
                          disabled={!hasTractor}
                          min={jobDateRange.start || ''}
                          max={jobDateRange.end || ''}
                        />
                        {!hasTractor && <p className="text-xs text-red-400 mt-1">Assign tractor first</p>}
                      </div>
                    </div>
                  </div>
                    {/* A line often needs more than one truck — e.g. 2 loads for the same leg. */}
                    {meta.ends && jobLines.length > 0 && (
                      <button type="button" onClick={() => addRow(row.lineItemId)}
                        className="w-full py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-2"
                        style={{ backgroundColor: '#F8F9FA', color: '#4A5568', border: '1px dashed #CBD5E1' }}>
                        <FaPlus size={11} /> Add another truck to {legLabelFor(jobLines, row.lineItemId) || 'this line'}
                      </button>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>

        {/* Drag Overlay */}
        <DragOverlay>
          {activeItem ? (
            <div className="p-4 bg-white rounded-lg shadow-lg border-2 border-green-500">
              {activeItem._kind === 'trailer' || (!activeItem._kind && activeItem.trailer_type) ? (
                <div>
                  <FaTrailer className="text-purple-600" />
                  <p className="font-bold">{activeItem.trailer_type || 'Trailer'}</p>
                  <p className="text-xs">{activeItem.registration_number}</p>
                </div>
              ) : (
                <div>
                  <FaTruck className="text-blue-600" />
                  <p className="font-bold">{unitLabel(activeItem) || 'Tractor'}</p>
                  {unitSubLabel(activeItem) && <p className="text-xs">{unitSubLabel(activeItem)}</p>}
                </div>
              )}
            </div>
          ) : null}
        </DragOverlay>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Link to={isAdminMode ? (adminSubbieId ? `/admin-scheduler?subbie=${adminSubbieId}` : '/admin-scheduler') : '/subbie-active-jobs'} className="px-6 py-2 rounded-lg text-sm font-medium transition-colors" style={{ backgroundColor: '#F8F9FA', color: '#4A5568', border: '1px solid #E8ECEF' }}>
            Cancel
          </Link>
          <button
            onClick={handleSubmit}
            disabled={submitting || rows.length === 0}
            className="px-6 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50 flex items-center gap-2"
            style={{ backgroundColor: '#00A86B' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#00995E'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#00A86B'}
          >
            <FaSave size={14} />
            {submitting ? 'Saving...' : 'Save Allocations'}
          </button>
        </div>
      </DndContext>

      {/* Allocated Units Section */}
      <div className="mt-8 space-y-6">
        {/* Active Units */}
        {activeAllocations.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: '#0A1929' }}>
              <FaTruck className="text-blue-500" /> Active Units
              <span className="text-sm font-normal text-gray-500">({activeAllocations.length})</span>
            </h2>
            <div className="space-y-4">
              {activeAllocations.map((unit, index) => {
                const isDelivered = unit.status === 'delivered';
                const isCompleted = unit.status === 'completed';
                const isComplete = isDelivered || isCompleted;
                const isPendingApproval = isDelivered && unit.pod_status === 'pending';
                
                let statusDisplay = getStatusLabel(unit.status);
                if (isDelivered && isPendingApproval) statusDisplay = '✅ Delivered (Pending Approval)';
                else if (isDelivered && unit.pod_status === 'approved') statusDisplay = '✅ Delivered (Approved)';
                else if (isCompleted) statusDisplay = '✅ Completed';
                
                return (
                  <div key={unit.id} className={`bg-white rounded-xl border p-6 ${isComplete ? 'opacity-70' : ''}`} style={{ borderColor: '#E8ECEF' }}>
                    <div className="flex flex-wrap items-center gap-4 mb-4">
                      <span className="text-sm font-bold text-navy">🚛 Unit {index + 1}</span>
                      <span className={getStatusBadge(unit.status)}>{statusDisplay}</span>
                      {!isCompleted && (
                        <button
                          onClick={() => handleRemoveUnit(unit.id)}
                          className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 ml-auto"
                        >
                          <FaTrash size={12} /> Remove
                        </button>
                      )}
                      {isCompleted && (
                        <span className="text-xs text-gray-400 flex items-center gap-1 ml-auto">
                          <FaCheckCircle size={12} /> Archived
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                      <div>
                        <p className="text-xs text-gray-500">Tractor</p>
                        <p className="font-medium text-sm">{unitLabel(unit.truck) || 'N/A'}</p>
                        <p className="text-xs text-gray-400">{unitSubLabel(unit.truck)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Trailer 1</p>
                        <p className="font-medium text-sm">{unit.trailer_1?.registration_number || 'N/A'}</p>
                        <p className="text-xs text-gray-400">{unit.trailer_1?.trailer_type}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Trailer 2</p>
                        <p className="font-medium text-sm">{unit.trailer_2?.registration_number || 'N/A'}</p>
                        <p className="text-xs text-gray-400">{unit.trailer_2?.trailer_type}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Driver</p>
                        <p className="font-medium text-sm">{unit.driver?.full_name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Arrival Date</p>
                        <p className="font-medium text-sm">{unit.arrival_date || 'N/A'}</p>
                      </div>
                    </div>

                    {!isCompleted && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {['pending', 'on_route_to_loading', 'onsite', 'loading', 'en_route', 'at_destination', 'delivered'].map(status => {
                          const isActive = unit.status === status;
                          const isClickable = canUpdateStatus(unit.status, status);
                          return (
                            <button
                              key={status}
                              onClick={() => isClickable && handleUpdateStatus(unit.id, status)}
                              disabled={!isClickable}
                              className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                                isActive 
                                  ? 'bg-green text-white' 
                                  : isClickable 
                                    ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' 
                                    : 'bg-gray-50 text-gray-300 cursor-not-allowed'
                              }`}
                            >
                              {getStatusLabel(status)}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2">
                      {unit.weighbridge_image_url && (
                        <span className="text-xs text-green-600 flex items-center gap-1">✅ Weighbridge uploaded</span>
                      )}
                      {unit.pod_image_url && (
                        <span className="text-xs text-green-600 flex items-center gap-1">✅ POD uploaded</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Completed Units */}
        {completedAllocations.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: '#047857' }}>
              <FaCheckCircle className="text-green-600" /> Completed Units
              <span className="text-sm font-normal text-gray-500">({completedAllocations.length})</span>
            </h2>
            <div className="space-y-4 opacity-75">
              {completedAllocations.map((unit, index) => (
                <div key={unit.id} className="bg-green-50 rounded-xl border border-green-200 p-6">
                  <div className="flex flex-wrap items-center gap-4 mb-4">
                    <span className="text-sm font-bold text-navy">🚛 Unit {index + 1}</span>
                    <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                      ✅ {unit.status === 'completed' ? 'Completed' : 'Delivered'}
                    </span>
                    <span className="text-xs text-gray-400 flex items-center gap-1 ml-auto">
                      <FaCheckCircle size={12} /> Archived
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                    <div>
                      <p className="text-xs text-gray-500">Tractor</p>
                      <p className="font-medium text-sm">{unitLabel(unit.truck) || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Trailer 1</p>
                      <p className="font-medium text-sm">{unit.trailer_1?.registration_number || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Trailer 2</p>
                      <p className="font-medium text-sm">{unit.trailer_2?.registration_number || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Driver</p>
                      <p className="font-medium text-sm">{unit.driver?.full_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Delivered</p>
                      <p className="font-medium text-sm">{unit.updated_at ? new Date(unit.updated_at).toLocaleDateString() : 'N/A'}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {unit.weighbridge_weight_mt && (
                      <span className="text-xs text-green-600 flex items-center gap-1">
                        ⚖️ {unit.weighbridge_weight_mt} MT
                      </span>
                    )}
                    {unit.weighbridge_image_url && (
                      <span className="text-xs text-green-600 flex items-center gap-1">✅ Weighbridge uploaded</span>
                    )}
                    {unit.pod_image_url && (
                      <span className="text-xs text-green-600 flex items-center gap-1">✅ POD uploaded</span>
                    )}
                    {unit.pod_number && (
                      <span className="text-xs text-gray-500 flex items-center gap-1">📋 POD #{unit.pod_number}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <ConfirmDialog
        isOpen={!!removeUnitTarget}
        onClose={() => setRemoveUnitTarget(null)}
        title="Remove Unit"
        message="Are you sure you want to remove this unit?"
        confirmLabel="Remove"
        danger
        onConfirm={performRemoveUnit}
      />
    </SchedulerShell>
  );
}

export default SubbieScheduler;
